import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {ArrowUpDown} from 'lucide-react';
import {FilterChip} from '@/components/shared/FilterChip';
import {DEFAULT_ITEM_SORT, type ItemSort} from '@/api/items';

// `short` is what the trigger shows after "Sort:", so the current order reads without opening it.
const SORT_GROUPS: ReadonlyArray<{
  label: string;
  options: ReadonlyArray<{value: ItemSort; label: string; short: string}>;
}> = [
  {
    label: 'Upload date',
    options: [
      {value: 'published_desc', label: 'Newest first', short: 'Newest uploaded'},
      {value: 'published_asc', label: 'Oldest first', short: 'Oldest uploaded'}
    ]
  },
  {
    label: 'Date collected',
    options: [
      {value: 'collected_desc', label: 'Newest first', short: 'Newest collected'},
      {value: 'collected_asc', label: 'Oldest first', short: 'Oldest collected'}
    ]
  },
  {
    label: 'Last updated',
    options: [
      {value: 'updated_desc', label: 'Newest first', short: 'Recently updated'},
      {value: 'updated_asc', label: 'Oldest first', short: 'Least recently updated'}
    ]
  }
];
const FLAGGED_GROUP = {
  label: 'Date flagged',
  options: [{value: 'flagged_desc' as const, label: 'Newest first', short: 'Recently flagged'}]
};
const SORT_OPTIONS = [FLAGGED_GROUP, ...SORT_GROUPS].flatMap((g) => g.options);

// The item sort control - shared by the investigations workspace and the Items page. The trigger
// always names the current order, per the design, so the default needs no highlight of its own.
export function SortMenu({
  value,
  onChange,
  flagged = false
}: {
  readonly value: ItemSort;
  readonly onChange: (next: ItemSort) => void;
  // Offers "Date flagged" - only meaningful over the caller's flagged items.
  readonly flagged?: boolean;
}) {
  const groups = flagged ? [FLAGGED_GROUP, ...SORT_GROUPS] : SORT_GROUPS;
  const option =
    SORT_OPTIONS.find((o) => o.value === value) ??
    SORT_OPTIONS.find((o) => o.value === DEFAULT_ITEM_SORT);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<FilterChip icon={<ArrowUpDown />} label={`Sort: ${option?.short}`} />}
      />
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => {
            onChange(next as ItemSort);
          }}>
          {groups.map((g, index) => (
            <div key={g.label}>
              {index > 0 ? <DropdownMenuSeparator /> : null}
              <DropdownMenuLabel>{g.label}</DropdownMenuLabel>
              {g.options.map((o) => (
                <DropdownMenuRadioItem key={o.value} value={o.value}>
                  {o.label}
                </DropdownMenuRadioItem>
              ))}
            </div>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
